/**
 * BenchManagePage - Coordinator work-bench management page (BLU-197)
 *
 * Mirrors BinManagePage / BinCreationSection + BinQRSection for work benches:
 * - Create benches in bulk (auto-incrementing BNCH-001, BNCH-002, …)
 * - List existing benches with status (EMPTY / IN_USE)
 * - Print / reprint bench QR labels (THP://BENCH:<label>)
 *
 * Work benches are the B2B / large-order analogue of bins: a labelled spot an
 * order is assigned to at the catcher station, then packed from. This page is
 * coordinator-only and the surface that bootstraps the bench-routing feature —
 * its nav entry stays hidden until at least one bench exists (dark by default).
 */
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutPanelTop, ArrowLeft, Plus, Loader2, QrCode, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { QRPreviewDialog } from '@/features/production-settings/components/QRPrintLayout';

const MAX_BENCHES_PER_REQUEST = 500;

// Bench status → pill styles (mirrors bin status language: muted=empty, amber=in use).
const BENCH_STATUS_STYLE: Record<string, string> = {
  EMPTY: 'bg-muted text-muted-foreground',
  IN_USE: 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200',
};

export function BenchManagePage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const locationId = selectedLocation?.id;

  const [count, setCount] = useState<number | ''>(5);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  const benchesQuery = trpc.bins.getBenches.useQuery(
    locationId ? { locationId } : undefined!,
    { enabled: !!locationId, staleTime: 15_000 },
  );

  // QR codes are generated lazily — only when the print dialog opens.
  const benchQRQuery = trpc.bins.generateBenchQRs.useQuery(
    locationId ? { locationId } : undefined!,
    { enabled: qrDialogOpen && !!locationId },
  );

  const createBenchesMutation = trpc.bins.createBenches.useMutation({
    onSuccess: (result) => {
      toast({ title: t('bench.created'), description: result.message, duration: 3000 });
      utils.bins.getBenches.invalidate();
      utils.bins.generateBenchQRs.invalidate();
    },
    onError: (error) => {
      toast({ title: t('bench.createFailed'), description: error.message, variant: 'destructive', duration: 5000 });
    },
  });

  const handleCreate = useCallback(() => {
    if (!locationId) return;
    const n = count === '' ? 0 : count;
    if (n < 1) return;
    createBenchesMutation.mutate({ locationId, count: n });
  }, [locationId, count, createBenchesMutation]);

  const benches = benchesQuery.data ?? [];
  const effectiveCount = count === '' ? 0 : count;

  // Location guard — mirror BinManagePage.
  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl animate-enter">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <LayoutPanelTop className="h-12 w-12 text-muted-foreground mb-4" aria-hidden="true" />
            <p className="text-lg font-medium">{t('bench.selectLocation')}</p>
            <p className="text-muted-foreground">{t('bench.selectLocationDesc')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <LayoutPanelTop className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{t('bench.manageTitle')}</h1>
            <p className="text-muted-foreground">{t('bench.manageDesc')}</p>
          </div>
        </div>
        <Button variant="outline" asChild>
          <Link to="/production/bins">
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
            {t('bins.dashboard')}
          </Link>
        </Button>
      </div>

      {/* Create benches */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t('bench.createTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <p className="text-sm text-muted-foreground">{t('bench.createBody')}</p>
            <div className="text-right shrink-0">
              <p className="text-2xl font-semibold tabular-nums">{benches.length}</p>
              <p className="text-xs text-muted-foreground">{t('bench.existing')}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Input
              id="bench-count"
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_BENCHES_PER_REQUEST}
              value={count}
              onChange={(e) => {
                if (e.target.value === '') {
                  setCount('');
                  return;
                }
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val)) {
                  setCount(Math.max(1, Math.min(MAX_BENCHES_PER_REQUEST, val)));
                }
              }}
              onBlur={() => {
                if (count === '' || count < 1) setCount(1);
              }}
              className="h-12 w-28 text-center text-lg font-medium tabular-nums"
              aria-label={t('bench.countAria')}
            />
            <Button
              onClick={handleCreate}
              disabled={createBenchesMutation.isPending || effectiveCount < 1}
              className="h-12 gap-2"
            >
              {createBenchesMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                <Plus className="h-5 w-5" aria-hidden="true" />
              )}
              {t('bench.createButton', { count: effectiveCount })}
            </Button>
          </div>

          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">{t('bench.createHint', { max: MAX_BENCHES_PER_REQUEST })}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bench list + QR printing */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-lg">{t('bench.listTitle')}</CardTitle>
            <QRPreviewDialog
              qrCodes={benchQRQuery.data ?? []}
              binType="BENCH"
              open={qrDialogOpen}
              onOpenChange={setQrDialogOpen}
            >
              <Button
                variant="outline"
                className="h-11 gap-2"
                disabled={benches.length === 0 || benchQRQuery.isFetching}
                aria-label={t('bench.printAllAria')}
                aria-busy={benchQRQuery.isFetching}
              >
                {benchQRQuery.isFetching ? (
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                ) : (
                  <QrCode className="h-5 w-5" aria-hidden="true" />
                )}
                {t('bench.printAll')}
              </Button>
            </QRPreviewDialog>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {benchesQuery.isLoading ? (
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-2">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : benches.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <LayoutPanelTop className="h-10 w-10 text-muted-foreground/50 mb-3" aria-hidden="true" />
              <p className="text-muted-foreground">{t('bench.empty')}</p>
              <p className="text-sm text-muted-foreground">{t('bench.emptyDesc')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-2">
              {benches.map((bench) => (
                <div
                  key={bench.id}
                  className={cn(
                    'h-14 rounded-lg flex flex-col items-center justify-center px-1',
                    BENCH_STATUS_STYLE[bench.status] ?? 'bg-muted text-muted-foreground',
                  )}
                  title={`${bench.label}: ${bench.status.replace(/_/g, ' ')}`}
                >
                  <span className="text-sm font-bold leading-none tabular-nums truncate max-w-full">
                    {bench.label}
                  </span>
                  <Badge variant="outline" className="mt-1 text-[9px] px-1 py-0 border-current/30 bg-transparent">
                    {bench.status.replace(/_/g, ' ')}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          <Separator className="my-4" />

          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              {t('bench.qrHint')}{' '}
              <code className="bg-background px-1 rounded">THP://BENCH:BNCH-001</code>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
